/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 08-09-2026
 *
 * De uitvoerders van de modelacties: wat in AngularJS `lokaal*` heette. Elke methode voert één
 * actie uit op de graaf en geeft terug wát daar verder mee moet — welke actielogregels erbij horen,
 * wat er naar de volgers gaat, en of er opgeslagen moet worden. De staart zelf (undo-punt, loggen,
 * versturen, snapshot, opslaan) doet de `SamenwerkprotocolService` in één vaste volgorde; zo staat
 * die volgorde op één plek in plaats van vijftien keer met de hand uitgeschreven.
 *
 * Een gewone klasse in eigendom van de baas, niet injecteerbaar — hetzelfde patroon als
 * `ModelSerialisatie` (golf a2) en `Documentstand` (golf a3).
 *
 * De graafmutaties zelf zitten in `DlModel`; hier staat wat daar bewust niet hoort: de conditieset
 * waarin een nieuw element terechtkomt (sessiestate), de vertaling naar protocolacties, en het
 * vastleggen van de logregels op het moment dat ze nog af te lezen zijn.
 */
import type {NodeSingular, Position} from 'cytoscape';
import {Sihwlog} from 'sihw-ngx';
import {elements} from 'dynalearn-common/lib/elements';
import {DlModel} from '../model/dl-model';
import type {Ballon, NieuweNodeData} from '../model/dl-model';
import {OpslagService} from '../model/opslag.service';
import type {Logintentie, Tekenaanleiding} from '../model/opslag.service';
import {CondtagService} from '../../services/condtag.service';
import type {CondtagState} from '../../services/condtag.service';
import {SamenwerkingService} from '../../services/samenwerking.service';
import type {Samenwerkactie} from './protocol.types';
import type {NormmatchMapping} from 'dynalearn-common/lib/normmatch.types';

/**
 * Wat een uitvoerder oplevert. Alle velden zijn optioneel: een actie die niets logt, niets
 * verstuurt en niets opslaat geeft een leeg object terug.
 */
export interface Actieuitkomst {
    /** De actielogregels, in volgorde, vastgelegd op het moment dat ze af te lezen waren. */
    logregels?: Logintentie[];
    /** Wat er als leider naar de volgers gaat. */
    bericht?: Samenwerkactie[];
    /** Wat er als leider over de simulatielijn naar de volgers gaat; die kent één actie per bericht. */
    simbericht?: {actie: string; args?: Record<string, unknown>};
    /** Of er opgeslagen wordt, en of dat een inhoudelijke wijziging is. */
    opslaan?: 'relevant' | 'irrelevant' | null;
    /** Of er zonder de gebruikelijke vertraging opgeslagen wordt. */
    direct?: boolean;
    /**
     * Waarom de stand veranderde, voor de opslagronde die erop volgt. Die doet een verse
     * bouwsuggestie-run, en zonder deze aanleiding weet die run niet dat hij op een teruggehaalde
     * stand draait — dan meldt hij de teruggekomen suggesties als nieuws.
     */
    aanleiding?: Tekenaanleiding;
    /**
     * Of er een snapshot aan de laatste logregel hangt. Standaard hangt hij eraan zodra er een
     * logregel geschreven is; één per samengestelde actie, anders slikt de dedup de tussenstappen.
     */
    snapshot?: boolean;
    /**
     * Of dit als inhoudelijke wijziging gemeld wordt terwijl er geen logregel is. Een volger logt
     * niet, maar zijn weergave moet wel mee — in AngularJS was dat de losse `model.contentChanged`.
     */
    inhoudGewijzigd?: boolean;
    /**
     * Een regel die niet met het model meereist maar meteen naar het backend gaat. Voor gebaren die
     * in de actielog horen maar niets aan het model veranderen, zoals het aanwijzen van een fout of
     * een stap in de simulatie. `doel` is waar de regel over gaat (`model` of `sim`).
     */
    directlog?: {actie: string; payload: object; volger?: string | null; doel?: 'model' | 'sim'};
}

export class Modelacties {

    constructor(
        private readonly dlModel: DlModel,
        private readonly opslag: OpslagService,
        private readonly condtagService: CondtagService,
        private readonly samenwerking: SamenwerkingService,
        private readonly log: Sihwlog
    ) {
    }

    /* ***************** DE UITVOERDERS ***************** */

    /**
     * Plaatst het element uit een add-actie op het canvas. Quantity's en quantity spaces hebben hun
     * eigen opbouw (een quantity krijgt meteen zijn afgeleide); de rest is één node.
     * @param positie waar hij komt te staan
     * @param actie de add-actie: het type, de eventuele parentnode en de extra data
     * @param [volger] het clientid van de volger die dit vroeg; die selecteert zelf, wij niet
     */
    public voegElementToe(positie: Position, actie: {type: string; node?: NodeSingular | null; data?: Record<string, unknown>},
                          volger?: string | null): Actieuitkomst {
        switch (actie.type) {
            case 'quantity':
                return this.voegQuantityToe(actie.node as NodeSingular, positie, volger);
            case 'quantity_space':
                return this.voegQuantitySpaceToe(actie.node as NodeSingular, positie,
                    actie.data?.['element'] as string, volger);
            default: {
                const node = this.maakNode({data: {type: actie.type}, position: positie},
                    actie.node ?? null, !volger);
                return {
                    logregels: [this.opslag.legElementLogVast('create', node, {}, volger)],
                    bericht: [{
                        actie: 'create',
                        node: node.json(),
                        select_in_origineel: true, //wij zouden deze selecteren als het lokaal begon
                        useCondtag: this.condtagService.condtag()
                    }],
                    opslaan: 'relevant'
                };
            }
        }
    }

    /**
     * Voegt een quantity toe, inclusief zijn afgeleide en de drie waarde-elementen.
     */
    private voegQuantityToe(entity: NodeSingular, positie: Position, volger?: string | null): Actieuitkomst {
        const selecteer = !volger;
        const resultaat = this.dlModel.addQuantity(entity, positie, selecteer,
            this.actieveTag(), this.actieveState());
        this.dlModel.updateConditieclasses(resultaat.quantity);
        for (const subnode of resultaat.subnodes) {
            this.dlModel.updateConditieclasses(subnode);
        }
        const bericht: Samenwerkactie[] = [{
            actie: 'create',
            node: resultaat.quantity.json(),
            select_in_origineel: true
        }];
        for (const subnode of resultaat.subnodes) {
            bericht.push({actie: 'create', node: subnode.json()}); //niet in condset
        }
        return {
            logregels: [this.opslag.legElementLogVast('create', resultaat.quantity, {}, volger)],
            bericht,
            opslaan: 'relevant'
        };
    }

    /**
     * Voegt een quantity space toe aan een quantity, met zijn eerste element. Staat er al een, dan
     * gebeurt er stilzwijgend niets — precies zoals in AngularJS.
     */
    private voegQuantitySpaceToe(qNode: NodeSingular, positie: Position, eersteType: string,
                                 volger?: string | null): Actieuitkomst {
        const selecteer = !volger;
        const resultaat = this.dlModel.addQuantitySpace(qNode, positie, eersteType, selecteer,
            this.actieveTag(), this.actieveState());
        if (!resultaat) {
            return {}; //er is er al een; stilzwijgend, net als in AngularJS
        }
        this.dlModel.updateConditieclasses(resultaat.qsNode);
        this.dlModel.updateConditieclasses(resultaat.qse);
        return {
            logregels: [this.opslag.legElementLogVast('create', resultaat.qsNode, {}, volger)],
            bericht: [
                {actie: 'create', node: resultaat.qsNode.json(), select_in_origineel: true},
                {actie: 'create', node: resultaat.qse.json()}
            ],
            opslaan: 'relevant'
        };
    }

    /**
     * Legt een relatie tussen twee nodes.
     * @param van de bronnode
     * @param naar de doelnode
     * @param type het relatietype
     * @param [nieuweId] het id dat de relatienode moet krijgen (van de leider meegekregen)
     * @param [selecteer] of de nieuwe relatie geselecteerd wordt
     * @param [volger] het clientid van de volger die dit vroeg
     */
    public legRelatie(van: NodeSingular, naar: NodeSingular, type: string, nieuweId?: string | false,
                      selecteer?: boolean, volger?: string | null): Actieuitkomst {
        //condtag/condstate zijn sessiestate; `DlModel` beslist daar niet over
        const def = elements.getDefinition(type);
        const relNode = this.dlModel.voegRelatieToe(van, naar, type, nieuweId || undefined, !!selecteer,
            def.neverConditional ? null : this.actieveTag(),
            def.neverConditional ? null : this.actieveState());
        this.dlModel.updateConditieclasses(relNode);

        if (this.samenwerking.isVolger()) {
            return {}; //een volger voert alleen uit; loggen en opslaan doet de leider
        }
        return {
            logregels: [this.opslag.legElementLogVast('create', relNode, {}, volger)],
            bericht: [{
                actie: 'addRelation',
                id: relNode.id(), //dat de edges niet gelijk zijn is geen ramp
                van: van.id(),
                naar: naar.id(),
                type,
                select_in_origineel: true,
                useCondtag: this.condtagService.condtag()
            }],
            opslaan: 'relevant'
        };
    }

    /**
     * Draait de argumenten van een relatie om.
     */
    public wisselArgumenten(node: NodeSingular, volger?: string | null): Actieuitkomst {
        const isVolger = this.samenwerking.isVolger();
        const logregels = isVolger ? [] : [this.opslag.legElementLogVast('modify', node, {switchargs: 'switched'}, volger)];
        this.dlModel.wisselArgumenten(node);
        return {
            logregels,
            bericht: [{actie: 'switchArgs', node: node.id()}], //geen invloed op condsets
            opslaan: isVolger ? null : 'relevant'
        };
    }

    /**
     * Wijzigt het type van een node.
     */
    public wijzigNodeType(node: NodeSingular, type: string, volger?: string | null): Actieuitkomst {
        const isVolger = this.samenwerking.isVolger();
        const logregels = isVolger ? []
            : [this.opslag.legElementLogVast('modify', node, {oldtype: node.data('type'), newtype: type}, volger)];
        this.dlModel.wijzigNodeType(node, type);
        return {
            logregels,
            bericht: [{actie: 'changeNode', node: node.id(), type}], //geen invloed op condset
            opslaan: isVolger ? null : 'relevant'
        };
    }

    /**
     * Zet of haalt de waarde bij een quantity-space-item weg. Alle bestaande waarden binnen dezelfde
     * conditiestate gaan eraf; stond de waarde al bij dit item, dan is het daarmee een weghalen.
     */
    public toggleQWaarde(node: NodeSingular, volger?: string | null): Actieuitkomst {
        const isVolger = this.samenwerking.isVolger();
        const logregels: Logintentie[] = [];
        const bericht: Samenwerkactie[] = [];
        const nodeId = node.id();
        //binnen dezelfde conditieset en -state (zie DlModel.updateConditieclasses)
        const selector = '[type = "quantity_value"].inconditiestate';
        let isVerwijderen = false;

        const qsNode = this.dlModel.qsNodeVoorItem(node);
        //allQsItems levert een collectie die per definitie alleen nodes bevat
        for (const item of qsNode ? this.dlModel.allQsItems(qsNode).toArray() as NodeSingular[] : []) {
            const bestaand = this.dlModel.outElements(item, selector);
            if (!bestaand.length) {
                continue;
            }
            const waarde = bestaand[0] as NodeSingular;
            if (!isVolger) {
                logregels.push(this.opslag.legElementLogVast('delete', waarde, {}, volger));
            }
            bericht.push({actie: 'delete', node: waarde.id(), useCondtag: this.condtagService.condtag()});
            this.dlModel.deleteNode(waarde);
            if (item.id() === nodeId) {
                isVerwijderen = true; //het was onze eigen waarde: klaar
                break;
            }
        }

        if (!isVerwijderen) {
            const nieuw = this.maakNode({
                data: {
                    type: 'quantity_value',
                    automovegroup: node.data('automovegroup') //alle elementen van deze qs bewegen samen
                },
                position: {x: node.position('x') - 30, y: node.position('y')}
            }, node, false);
            if (!isVolger) {
                logregels.push(this.opslag.legElementLogVast('create', nieuw, {}, volger));
            }
            bericht.push({actie: 'create', node: nieuw.json(), useCondtag: this.condtagService.condtag()});
        }
        return {logregels, bericht, opslaan: isVolger ? null : 'relevant'};
    }

    /**
     * Zet of haalt de waarde bij een afgeleide weg. Er kan er maar één zijn; een globale (niet
     * conditionele) waarde sluit bovendien de exogenen uit, dus die gaan er dan af.
     */
    public toggleDWaarde(node: NodeSingular, volger?: string | null): Actieuitkomst {
        const isVolger = this.samenwerking.isVolger();
        const logregels: Logintentie[] = [];
        const bericht: Samenwerkactie[] = [];
        const nodeId = node.id();
        const afgeleideFilter = '[type = "derivative_plus"],[type = "derivative_zero"],[type = "derivative_min"]';
        let item = this.dlModel.getElement(node.data('derivative') as string) as unknown as NodeSingular;
        let isVerwijderen = false;

        //loop over de afgeleide-elementen (een soort linked list) tot we een waarde vinden
        let volgende = item ? this.dlModel.outElements(item, afgeleideFilter) : null;
        while (volgende?.length) {
            item = volgende[0] as NodeSingular;
            const waarde = this.dlModel.outElements(item, '[type = "derivative_value"].inconditiestate');
            if (waarde.length) {
                const teVerwijderen = waarde[0] as NodeSingular;
                if (!isVolger) {
                    logregels.push(this.opslag.legElementLogVast('delete', teVerwijderen, {}, volger));
                }
                bericht.push({actie: 'delete', node: teVerwijderen.id()});
                this.dlModel.deleteNode(teVerwijderen);
                if (item.id() === nodeId) {
                    isVerwijderen = true;
                }
                break; //sowieso: er kan er maar één zijn
            }
            volgende = this.dlModel.outElements(item, afgeleideFilter);
        }

        if (!isVerwijderen) {
            if (!this.condtagService.condtag()) {
                //een globale d-waarde en een exogeen sluiten elkaar uit
                const quantity = this.dlModel.getQuantity(node);
                for (const subel of quantity ? quantity.successors().toArray() : []) {
                    if (!elements.isSubtypeOf(subel.data('type') as string, 'q_exo')) {
                        continue;
                    }
                    if (!isVolger) {
                        logregels.push(this.opslag.legElementLogVast('delete', subel as NodeSingular, {}, volger));
                    }
                    bericht.push({actie: 'delete', node: subel.id()});
                    this.dlModel.deleteNode(subel as NodeSingular);
                }
            }
            const nieuw = this.maakNode({
                data: {
                    type: 'derivative_value',
                    automovegroup: node.data('automovegroup')
                },
                position: {x: node.position('x') - 30, y: node.position('y')}
            }, node, false);
            if (!isVolger) {
                logregels.push(this.opslag.legElementLogVast('create', nieuw, {}, volger));
            }
            bericht.push({actie: 'create', node: nieuw.json()});
        }
        return {logregels, bericht, opslaan: isVolger ? null : 'relevant'};
    }

    /**
     * Voegt een element toe aan een quantity space, boven- of onderaan.
     */
    public voegQsElementToe(qsNode: NodeSingular, positie: 'top' | 'bottom', volger?: string | null): Actieuitkomst {
        const qse = this.maakQsElement(qsNode, positie, !volger);
        return {
            logregels: [this.opslag.legElementLogVast('create', qse, {location: positie}, volger)],
            bericht: [{
                actie: 'addQSE',
                positie,
                qs: qsNode.id(),
                id: qse.id(),
                select_in_origineel: true
                //niet in condset
            }],
            opslaan: 'relevant'
        };
    }

    /**
     * Zet een quantity-space-punt op nul, of haalt die markering er juist af. Er kan er maar één per
     * quantity space zijn, dus een bestaande gaat er eerst af.
     *
     * Let op de volgorde van het loggen: `zetIsZero` verandert de getoonde naam, en de logregel hoort
     * de naam te dragen die bij die stand past — bij het weghalen dus ná, bij het zetten vóór.
     */
    public toggleQsNulpunt(node: NodeSingular, volger?: string | null): Actieuitkomst {
        const isVolger = this.samenwerking.isVolger();
        const logregels: Logintentie[] = [];
        let isToggle = false;

        const qsNode = this.dlModel.qsNodeVoorItem(node);
        //allQsItems levert een collectie die per definitie alleen nodes bevat
        for (const item of qsNode ? this.dlModel.allQsItems(qsNode).toArray() as NodeSingular[] : []) {
            if (!item.data('isZero')) {
                continue;
            }
            this.dlModel.zetIsZero(item, false);
            if (!isVolger) {
                logregels.push(this.opslag.legElementLogVast('modify', item, {isZero: false}, volger));
            }
            if (node.same(item)) {
                isToggle = true; //het was deze: alleen weghalen
                break;
            }
        }
        if (!isToggle) {
            if (!isVolger) {
                logregels.push(this.opslag.legElementLogVast('modify', node, {isZero: true}, volger));
            }
            this.dlModel.zetIsZero(node, true);
        }
        return {
            logregels,
            bericht: [{actie: 'qsZero', node: node.id()}], //niet in condset
            opslaan: isVolger ? null : 'relevant'
        };
    }

    /**
     * Zet of haalt een exogeen weg bij een quantity. Er kan er maar één zijn, en hij verdraagt geen
     * globale afgeleide-waarde: beide gaan er eerst af.
     */
    public toggleExogeen(node: NodeSingular, exoType: string, volger?: string | null): Actieuitkomst {
        const isVolger = this.samenwerking.isVolger();
        const logregels: Logintentie[] = [];
        const bericht: Samenwerkactie[] = [];

        for (const subnode of this.dlModel.outElements(node, 'node').toArray()) {
            if (!elements.isSubtypeOf(subnode.data('type') as string, 'q_exo')) {
                continue;
            }
            if (!isVolger) {
                logregels.push(this.opslag.legElementLogVast('delete', subnode as NodeSingular, {}, volger));
            }
            bericht.push({actie: 'delete', node: subnode.id()});
            this.dlModel.deleteNode(subnode as NodeSingular);
        }
        //een afgeleide-waarde zonder conditieset kan er ook niet naast staan
        for (const dval of node.successors('[type = "derivative_value"][!condtag]').toArray()) {
            if (!isVolger) {
                logregels.push(this.opslag.legElementLogVast('delete', dval as NodeSingular, {}, volger));
            }
            bericht.push({actie: 'delete', node: dval.id()});
            this.dlModel.deleteNode(dval as NodeSingular);
        }

        const nieuw = this.maakNode({
            data: {type: exoType},
            position: {x: node.position('x') - 60, y: node.position('y') - 60}
        }, node, !volger);
        bericht.push({actie: 'create', node: nieuw.json(), select_in_origineel: true});
        if (!isVolger) {
            logregels.push(this.opslag.legElementLogVast('create', nieuw, {}, volger));
        }
        return {logregels, bericht, opslaan: isVolger ? null : 'relevant'};
    }

    /**
     * Zet of haalt de "genereer alle waarden"-markering bij een quantity weg.
     */
    public toggleAlleWaarden(node: NodeSingular, volger?: string | null): Actieuitkomst {
        const isVolger = this.samenwerking.isVolger();
        const logregels: Logintentie[] = [];
        const bericht: Samenwerkactie[] = [];
        const bestaand = this.dlModel.outElements(node, '[type="quantity_allvalues"]')[0] as NodeSingular | undefined;

        if (bestaand) {
            if (!isVolger) {
                logregels.push(this.opslag.legElementLogVast('delete', bestaand, {}, volger));
            }
            bericht.push({actie: 'delete', node: bestaand.id()});
            this.dlModel.deleteNode(bestaand);
        } else {
            const nieuw = this.maakNode({
                data: {type: 'quantity_allvalues'},
                position: {x: node.position('x') + 60, y: node.position('y') - 60} //rechtsboven
            }, node, !volger);
            bericht.push({actie: 'create', node: nieuw.json(), select_in_origineel: true}); //niet in condset
            if (!isVolger) {
                logregels.push(this.opslag.legElementLogVast('create', nieuw, {}, volger));
            }
        }
        return {logregels, bericht, opslaan: isVolger ? null : 'relevant'};
    }

    /**
     * Wijzigt de naam van een node. De genormaliseerde naam gaat de lijn op, zodat iedereen dezelfde
     * waarde krijgt als wat er in de graaf staat.
     * @param alleenLokaal een volger die een leiderbericht uitvoert: niet loggen, niet opslaan
     */
    public wijzigNaam(node: NodeSingular, naam: string, alleenLokaal?: boolean, volger?: string | null): Actieuitkomst {
        const oudeNaam = node.data('nodename');
        const genormaliseerd = this.dlModel.wijzigNaam(node, naam);
        if (alleenLokaal) {
            return {};
        }
        return {
            logregels: [this.opslag.legElementLogVast('modify', node,
                {oldname: oudeNaam, newname: genormaliseerd}, volger)],
            bericht: [{actie: 'wijzigNaam', node: node.id(), naam: genormaliseerd}], //geen invloed op condset
            opslaan: 'relevant'
        };
    }

    /**
     * Verwijdert een node met alles wat eronder hangt. Quantity-space-punten en -intervallen hebben
     * hun eigen afhandeling: daar wordt de rest van de ruimte hersteld.
     * @param alleenLokaal een volger die een leiderbericht uitvoert: niet loggen, niet opslaan
     */
    public verwijderNode(node: NodeSingular, alleenLokaal?: boolean, volger?: string | null): Actieuitkomst {
        const nodeId = node.id();
        const type = node.data('type') as string;
        //de logregel moet vastliggen vóór de mutatie: daarna zijn de route en de argumenten weg
        const logregels = alleenLokaal ? [] : [this.opslag.legElementLogVast('delete', node, {}, volger)];

        if (type === 'quantity_space_point' || type === 'quantity_space_interval') {
            this.dlModel.deleteQuantitySpaceElement(node);
        } else {
            this.dlModel.deleteNode(node);
        }
        return {
            logregels,
            bericht: [{actie: 'delete', node: nodeId}], //gaan vanzelf goed in condset
            opslaan: alleenLokaal ? null : 'relevant'
        };
    }

    /**
     * Zet posities van nodes. Sla alleen op als er iets bewoog dat niet bij de simulatie hoort — een
     * versleepte simulatienode verandert het model niet.
     * @param alsLeider stuur de posities door naar de volgers
     * @param [fit] het hele model is verschoven (snapNorm bij de uitvoerder), dus het beeld moet mee
     */
    public zetPosities(posities: Array<{id: string; pos: Position}>, alsLeider: boolean, fit = false): Actieuitkomst {
        const nietsimulatiemove = this.dlModel.zetPosities(posities, fit);
        return {
            bericht: alsLeider ? [{actie: 'positie', posities}] : [], //staat los van condset
            opslaan: nietsimulatiemove ? 'irrelevant' : null
        };
    }

    /**
     * Schuift het model naar de vorm van het normmodel en stuurt de nieuwe posities naar de volgers, langs
     * de gewone `positie`-regel. Geen logregel en geen snapshot, net als in AngularJS; wel opslaan, als
     * irrelevante wijziging.
     * @param mappingsOpWerk de mapping per werkelement uit de huidige normmatch
     */
    public snapNaarNorm(mappingsOpWerk: Record<string, NormmatchMapping>): Actieuitkomst {
        const posities = this.dlModel.snapNaarNorm(mappingsOpWerk);
        return {
            //`fit`: het model komt ergens anders te staan, dus ook bij de ontvangers moet het beeld mee
            //(testronde 4d, test 13). `snapNaarNorm` zelf zet de vlag al voor de uitvoerder
            bericht: posities.length ? [{actie: 'positie', posities, fit: true}] : [],
            opslaan: 'irrelevant'
        };
    }

    /**
     * Zet of wist de ballon bij een node.
     */
    public zetBallon(ballon: Ballon | null, nodeId: string, volger?: string | null): Actieuitkomst {
        const node = this.dlModel.getElement(nodeId);
        if (!node || node.empty()) {
            this.log.warn(`zetBallon voor een node die er niet is: ${nodeId}`);
            return {};
        }
        this.dlModel.setBallon(node, ballon);
        if (this.samenwerking.isVolger()) {
            return {bericht: [{actie: 'setBallon', ballon, node: nodeId}]};
        }
        return {
            logregels: [this.opslag.legLogVast(ballon ? 'ballon' : 'ballon_wegklik',
                this.dlModel.route(node), node.data('type') as string,
                (ballon ?? {}) as Record<string, unknown>, volger)],
            bericht: [{actie: 'setBallon', ballon, node: nodeId}],
            opslaan: 'relevant',
            snapshot: true
        };
    }

    /**
     * Voert een undo of redo uit en stuurt de nieuwe stand als geheel naar de volgers: die kunnen
     * niet stap voor stap terugrekenen.
     */
    public undoRedo(redo: boolean, volger?: string | null): Actieuitkomst {
        if (redo) {
            this.opslag.redo();
        } else {
            this.opslag.undo();
        }
        return {
            logregels: [this.opslag.legLogVast(redo ? 'redo' : 'undo', 'model', 'model', {}, volger, false)],
            bericht: [{actie: 'setModeldata', data: this.dlModel.modelData()}], //inclusief condset
            opslaan: 'relevant',
            //de opslagronde doet een tweede bouwsuggestie-run, in dezelfde tick als die van de
            //hertekening. Zonder deze aanleiding draait die als een gewone berekening en telt alles
            //wat de undo terugbracht als nieuws
            aanleiding: 'herstel',
            snapshot: true
        };
    }

    /* ***************** DE CONDITIESETS ***************** */

    /**
     * Maakt een conditieset, hernoemt hem of haalt hem weg. De drie takken delen hun staart: een
     * undo-punt vooraf (de tags krijgen nieuwe data, ook bij een volger), één logregel, en het
     * bericht naar de volgers.
     *
     * Een nieuwe set is géén inhoudelijke wijziging: hij bestaat pas echt als er elementen in zitten.
     * Hernoemen en verwijderen zijn dat wel — bij verwijderen gaan de elementen mee, en dat gebeurt
     * bij iedereen lokaal binnen dit ene bericht, dus zonder aparte delete-regels.
     * @param oudeNaam de bestaande set, of `null` om er een te maken
     * @param nieuweNaam de nieuwe naam, of `null` om te verwijderen
     */
    public wijzigConditieset(oudeNaam: string | null, nieuweNaam: string | null,
                             volger?: string | null): Actieuitkomst {
        const isVolger = this.samenwerking.isVolger();
        const bericht: Samenwerkactie[] = [{actie: 'changeCondtag', oldname: oudeNaam, newname: nieuweNaam}];
        let logregels: Logintentie[] = [];
        let opslaan: 'relevant' | null = isVolger ? null : 'relevant';

        switch (true) {
            case !oudeNaam && !!nieuweNaam:
                logregels = [this.opslag.legLogVast('cond_new', nieuweNaam, 'model', {}, volger)];
                this.condtagService.zetZichtbaar(nieuweNaam!, true);
                this.condtagService.zetActieveTag({tag: nieuweNaam!, state: 'cond'});
                opslaan = null; //een lege conditieset verandert het model niet
                break;
            case !!oudeNaam && !!nieuweNaam:
                logregels = [this.opslag.legLogVast('cond_rename', oudeNaam, 'model', {nieuw: nieuweNaam}, volger)];
                //de tag houdt zijn zichtbaarheid; was hij actief, dan verhuist de actieve tag mee
                this.condtagService.hernoemTag(oudeNaam!, nieuweNaam!);
                this.dlModel.hernoemCondtag(oudeNaam!, nieuweNaam!);
                break;
            default:
                logregels = [this.opslag.legLogVast('cond_delete', oudeNaam, 'model', {}, volger)];
                if (this.condtagService.condtag()?.tag === oudeNaam) {
                    this.condtagService.zetActieveTag(null);
                    this.condtagService.zetOpen(false);
                }
                for (const node of this.dlModel.nodesInCondtag(oudeNaam!).toArray() as NodeSingular[]) {
                    this.dlModel.deleteNode(node);
                }
                this.condtagService.wisTag(oudeNaam!);
                break;
        }
        this.dlModel.updateConditieclasses();
        return {logregels: isVolger ? [] : logregels, bericht, opslaan};
    }

    /**
     * Maakt een conditieset actief, of geen enkele. Het paneel gaat er sowieso mee dicht.
     */
    public zetConditieset(tag: string | null, state: 'cond' | 'cons' | null,
                          volger?: string | null): Actieuitkomst {
        const isVolger = this.samenwerking.isVolger();
        if (tag) {
            this.condtagService.zetActieveTag({tag, state: state ?? 'cond'});
            this.condtagService.zetZichtbaar(tag, true); //ook weergeven
        } else {
            this.condtagService.zetActieveTag(null);
        }
        this.dlModel.updateConditieclasses();
        this.condtagService.zetOpen(false);
        return {
            logregels: isVolger ? [] : [tag
                ? this.opslag.legLogVast('cond_set', tag, 'model', {state}, volger)
                : this.opslag.legLogVast('cond_reset', '-', 'model', {}, volger)],
            bericht: [{actie: 'setCondtag', condtag: tag, state}],
            opslaan: isVolger ? null : 'irrelevant'
        };
    }

    /**
     * Toont of verbergt de elementen van een conditieset. De actieve set blijft altijd zichtbaar:
     * daarin wordt gewerkt.
     */
    public toonConditieset(tag: string, toon: boolean, volger?: string | null): Actieuitkomst {
        const isVolger = this.samenwerking.isVolger();
        if (!isVolger && this.condtagService.condtag()?.tag === tag) {
            return {}; //dit is de actieve set; die blijft aan
        }
        //de toggle draait op de eigen stand, niet op de meegestuurde waarde (zo deed AngularJS het)
        this.condtagService.zetZichtbaar(tag, !this.condtagService.conditioneleTags()[tag]);
        this.dlModel.updateConditieclasses();
        return {
            logregels: isVolger ? [] : [this.opslag.legLogVast('cond_view', tag, 'model', {show: toon}, volger)],
            bericht: [{actie: 'setViewtag', condtag: tag, show: toon}],
            opslaan: isVolger ? null : 'irrelevant'
        };
    }

    /**
     * Neemt de conditieset-stand van de leider over. Alleen voor een volger: een nieuwe deelnemer
     * krijgt zo in één keer welke set actief is en welke er zichtbaar zijn.
     */
    public neemConditiesetsOver(actieveTag: CondtagState | null,
                                tags: Record<string, boolean>): Actieuitkomst {
        this.condtagService.zetActieveTag(actieveTag);
        this.condtagService.zetTags(tags);
        //of het paneel openstaat nemen we niet over: dat is lokaal gedrag (09-09-2026)
        this.dlModel.updateConditieclasses();
        return {};
    }

    /* ***************** DE UITVOERING VAN EEN LEIDERBERICHT ***************** */

    /**
     * Neemt een node over die de leider heeft gemaakt. De conditieset komt **van de lijn** mee en
     * wordt hier niet opnieuw gestempeld: alleen zo staan alle deelnemers gegarandeerd gelijk
     * (besluit gebruiker 08-09-2026). De lokale stempel geldt alleen voor wat hier zelf begint.
     * @param nodeData de `json()` van de node bij de leider
     * @param selecteer of wij hem selecteren (alleen als wij de actie begonnen)
     */
    public neemNodeOver(nodeData: NieuweNodeData, selecteer: boolean): NodeSingular {
        const parentId = nodeData.data['parentId'] as string | undefined;
        const parent = parentId ? this.dlModel.getElement(parentId) : null;
        if (parentId && (!parent || parent.empty())) {
            throw new Error('UNMATCH'); //de parent kennen we niet: onze graaf loopt uit de pas
        }
        const node = this.dlModel.addCyNode(nodeData, parent, selecteer);
        this.dlModel.updateConditieclasses(node);
        return node;
    }

    /**
     * Neemt een quantity-space-element over dat de leider heeft gemaakt.
     */
    public neemQsElementOver(qsNode: NodeSingular, positie: 'top' | 'bottom', selecteer: boolean,
                             elementType?: string, id?: string): NodeSingular {
        return this.maakQsElement(qsNode, positie, selecteer, elementType, id);
    }

    /* ***************** HELPERS ***************** */

    /**
     * Maakt een node en stempelt de conditieset erop waarin hij hoort. Elementen die nooit in een
     * conditieset zitten (`neverConditional`, zoals een calc) krijgen er geen.
     */
    private maakNode(nodeData: NieuweNodeData, parent: NodeSingular | null, selecteer: boolean): NodeSingular {
        const def = elements.getDefinition(nodeData.data['type'] as string);
        nodeData.data['condtag'] = def.neverConditional ? null : this.actieveTag();
        nodeData.data['condstate'] = def.neverConditional ? null : this.actieveState();
        const node = this.dlModel.addCyNode(nodeData, parent, selecteer);
        this.dlModel.updateConditieclasses(node);
        return node;
    }

    /**
     * Maakt een quantity-space-element in de actieve conditieset.
     */
    private maakQsElement(qsNode: NodeSingular, positie: 'top' | 'bottom', selecteer: boolean,
                          elementType?: string, id?: string): NodeSingular {
        const node = this.dlModel.addQuantitySpaceElement(qsNode, positie, selecteer, elementType, id,
            this.actieveTag(), this.actieveState());
        this.dlModel.updateConditieclasses(node);
        return node;
    }

    /**
     * De naam van de actieve conditieset, of `null`.
     */
    private actieveTag(): string | null {
        return this.condtagService.condtag()?.tag ?? null;
    }

    /**
     * De actieve conditiestate (`cond`/`cons`), of `null`.
     */
    private actieveState(): string | null {
        return this.condtagService.condtag()?.state ?? null;
    }
}
